import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { User } from "db/definitions";
import { useEffect, useState } from "react";
import { recordSentry } from "utils/recordSentry";
import { getTotalFieldInstanceDiskSize } from "../db/queries/fieldInstanceQueries";
import { getTotalFieldTemplateDiskSize } from "../db/queries/fieldTemplateQueries";
import { getTotalFormGroupDiskSize } from "../db/queries/formGroupQueries";
import { getTotalFormInstanceDiskSize } from "../db/queries/formInstanceQueries";
import { getTotalFormTemplateDiskSize } from "../db/queries/formTemplateQueries";
import { getTotalImageDiskSize } from "../db/queries/imageQueries";
import { getTotalOptionDiskSize } from "../db/queries/optionQueries";
import { getTotalProjectDiskSize } from "../db/queries/projectQueries";
import { getTotalUserDiskSize, replaceUserInDatabase } from "../db/queries/userQueries";

function useOffline() {
	const [loading, setLoading] = useState(true);
	const [notConnected, setNotConnected] = useState(false);
	const [online, setOnline] = useState(() => {
		const localStorageAnswer = window.localStorage.getItem("online");
		if (localStorageAnswer !== null) return localStorageAnswer === "true" ? true : false;
		window.localStorage.setItem("online", "true");
		return true;
	});

	const [persisted, setPersisted] = useState(false);
	const [quota, setQuota] = useState(0);
	const [quotaReadable, setQuotaReadable] = useState("0 B");
	const [usage, setUsage] = useState(0);
	const [usageReadable, setUsageReadable] = useState("0 B");
	const [userErrorMessage, setUserErrorMessage] = useState("");
	const [estimateReadable, setEstimateReadable] = useState("");

	async function checkNetworkConnection() {
		const endpoints = ["https://ifconfig.me", "https://api.ipify.org", "https://ipinfo.io/ip"];

		for (const endpoint of endpoints) {
			try {
				const response = await fetch(endpoint, { cache: "no-store" });
				if (response.ok) {
					return response;
				}
			} catch (error) {
				console.warn(`Network check failed for ${endpoint}:`, error);
			}
		}
		throw new Error("All network endpoints failed");
	}

	useEffect(() => {
		async function checkNetwork() {
			const localStorageAnswer = window.localStorage.getItem("online");
			try {
				const response = await checkNetworkConnection();
				if (localStorageAnswer !== null) {
					if (response.ok) {
						const onlineStatus = localStorageAnswer === "true" ? true : false;
						setOnline(onlineStatus);
						setLoading(false);
						return onlineStatus;
					} else {
						window.localStorage.setItem("online", "false");
						setOnline(false);
						setLoading(false);
						return false;
					}
				} else {
					if (response.ok) {
						window.localStorage.setItem("online", "true");
						setOnline(true);
						setLoading(false);
						return true;
					} else {
						window.localStorage.setItem("online", "false");
						setOnline(false);
						setLoading(false);
						return false;
					}
				}
			} catch (error) {
				window.localStorage.setItem("online", "false");
				setOnline(false);
				setLoading(false);
			}
			return false;
		}

		function isIos() {
			return /(iPhone|iPod|iPad)/i.test(navigator.userAgent);
		}
		async function persistStorage() {
			if (navigator.storage?.persisted && navigator.storage?.persist) {
				const result = await navigator.storage.persisted();
				if (result) {
					setPersisted(true);
					setUserErrorMessage("");
				} else {
					// Try to persist storage
					const persistResult = await navigator.storage.persist();
					if (persistResult) {
						setUserErrorMessage("");
						setPersisted(true);
					} else {
						// This is so we can access the sync menu on local host for those that cant.
						if (import.meta.env.DEV && window.location.hostname.includes("localhost")) {
							setPersisted(true);
							return;
						}
						setUserErrorMessage("The persist function returned false. Try adding Record to the home screen");
						console.error("THE PERSIST FUNCTION RETURNED FALSE. IS THE WEBSITE 'IMPORTANT?' (DOWNLOADED PWA)");
					}
				}
			} else {
				if (isIos()) {
					const versionNumberMatch = window.navigator.userAgent.match(/OS (\d)?\d_\d(_\d)?/i);
					if (versionNumberMatch && versionNumberMatch.length >= 1) {
						const versionNumber = versionNumberMatch[0];
						const releaseInfo = versionNumber.split("_");
						if (parseInt(releaseInfo[0]) < 15 || (parseInt(releaseInfo[0]) === 15 && parseInt(releaseInfo[1]) < 2)) {
							console.error(`IOS 15.2 or later supports storage persist`);
							setUserErrorMessage(
								`IOS 15.2 or later supports storage persist. Make sure you're using Safari.\nYour IOS version: ${releaseInfo[0]}.${releaseInfo[1]}\nYour user agent: ${navigator.userAgent}`,
							);
						} else {
							console.error(`Unknown IOS issue persisting storage`);
							setUserErrorMessage(
								`Unknown IOS issue persisting storage. Make sure you're using Safari.\nYour IOS version: ${releaseInfo[0]}.${releaseInfo[1]}\nYour user agent: ${navigator.userAgent}`,
							);
						}
					} else {
						const versionNumberSecondTry = navigator.userAgent.match(/Version\/(\d)?\d.\d(.\d)?/i);
						if (versionNumberSecondTry) {
							const splitVersion = versionNumberSecondTry[0].split(".");
							if (parseInt(splitVersion[0]) < 15 || (parseInt(splitVersion[0]) === 15 && parseInt(splitVersion[1]) < 2)) {
								console.error(
									`IOS 15.2 or later supports storage persist. You have ${splitVersion[0]}.${splitVersion[1]} installed.\nUpdate your IOS, and make sure you're using the downloaded PWA\n Your user agent: ${navigator.userAgent}`,
								);
								setUserErrorMessage(
									`IOS 15.2 or later supports storage persist. You have ${splitVersion[0]}.${splitVersion[1]} installed.\nUpdate your IOS, and make sure you're using the downloaded PWA\n Your user agent: ${navigator.userAgent}`,
								);
							} else {
								console.error(
									`Unknown IOS issue persisting storage. You have ${splitVersion[0]}.${splitVersion[1]} installed.\nUpdate your IOS, and make sure you're using the downloaded PWA\n Your user agent: ${navigator.userAgent}`,
								);
								setUserErrorMessage(
									`Unknown IOS issue persisting storage. You have ${splitVersion[0]}.${splitVersion[1]} installed.\nUpdate your IOS, and make sure you're using the downloaded PWA\n Your user agent: ${navigator.userAgent}`,
								);
							}
						}
					}
				} else {
					if (!navigator.userAgent.includes("Chrome/")) {
						setUserErrorMessage("Unsupported browser. Record is supported in Chrome (For desktop/laptop) and Safari (ipad).");
						console.error(`UNSUPPORTED BROWSER`);
					} else {
						const versionNumber = navigator.userAgent.split("Chrome/")[1];
						if (parseInt(versionNumber.split(".")[0]) < 55) {
							setUserErrorMessage(`You need chrome 55 or greater, you have ${versionNumber}`);
							console.error(`YOU NEED CHROME 55 OR GREATER. YOU HAVE ${versionNumber}`);
						} else {
							setUserErrorMessage("Unknown issue. Contact tech support. Cannot determine browser");
							console.error(`UNKNOWN ISSUE. CONTACT TECH SUPPORT. Cannot determine browser`);
						}
					}
				}
			}
		}
		persistStorage();
		checkNetwork();
	}, []);

	useEffect(() => {
		/**
		 * Format bytes as human-readable text.
		 * https://stackoverflow.com/a/14919494/18673230
		 * @param bytes Number of bytes.
		 * @param si True to use metric (SI) units, aka powers of 1000. False to use binary (IEC), aka powers of 1024.
		 * @param dp Number of decimal places to display
		 * @return Formatted string.
		 */
		function humanFileSize(bytes: number, si = false, dp = 2): string {
			const thresh = si ? 1000 : 1024;

			if (Math.abs(bytes) < thresh) {
				return bytes + " B";
			}

			const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
			let u = -1;
			const r = 10 ** dp;

			do {
				bytes /= thresh;
				++u;
			} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

			return bytes.toFixed(dp) + " " + units[u];
		}
		async function getQuota() {
			if (navigator.storage?.estimate) {
				const estimate = await navigator.storage.estimate();
				const quotaEstimate = estimate.quota;
				if (quotaEstimate) {
					setQuota(quotaEstimate);
					setQuotaReadable(humanFileSize(quotaEstimate));
				}
				const usageEstimate = estimate.usage;
				if (usageEstimate) {
					setUsage(usageEstimate);
					setUsageReadable(humanFileSize(usageEstimate));
				}
			} else {
				function isIos() {
					return /(iPhone|iPod|iPad|Macintosh)/i.test(navigator.userAgent);
				}
				if (isIos()) {
					const versionNumberMatchArray = window.navigator.userAgent.match(/Version\/([^ ]+)/);
					if (versionNumberMatchArray && versionNumberMatchArray.length >= 2) {
						const versionNumber = versionNumberMatchArray[1];
						if (parseInt(versionNumber) < 17) {
							console.error(`IOS 17 or later supports estimate`);
						} else {
							console.error(`Unknown IOS issue getting usage estimate`);
						}
					} else {
						const versionNumberSecondTry = navigator.userAgent.match(/Version\/(\d)?\d.\d(.\d)?/i);
						if (versionNumberSecondTry) {
							const splitVersion = versionNumberSecondTry[0].split(".");
							if (parseInt(splitVersion[0]) < 17) {
								console.error(`IOS 17 or later supports estimate. You have ${splitVersion[0]}`);
							} else {
								console.error(`Unknown IOS issue getting usage estimate`);
							}
						}
					}
				} else {
					// Real human technology
					if (!navigator.userAgent.includes("Chrome/")) {
						setUserErrorMessage("Unsupported browser. Record is supported in Chrome.");
						console.error(`UNSUPPORTED BROWSER`);
					} else {
						const versionNumber = navigator.userAgent.split("Chrome/")[1];
						if (parseInt(versionNumber.split(".")[0]) < 61) {
							setUserErrorMessage(`You need chrome 61 or greater, you have ${versionNumber}`);
							console.error(`YOU NEED CHROME 61 OR GREATER. YOU HAVE ${versionNumber}`);
						} else {
							setUserErrorMessage("Unknwon issue. Contact tech support.");
							console.error(`UNKNOWN ISSUE. CONTACT TECH SUPPORT`);
						}
					}
				}
				// Okay, since we don't have the estimate api, we gotta count it by hand. Since we don't have an average file size for the imageBinaries, they aren't included
				const totalSizes = await Promise.all([
					getTotalFieldInstanceDiskSize(),
					getTotalFieldTemplateDiskSize(),
					getTotalFormGroupDiskSize(),
					getTotalFormInstanceDiskSize(),
					getTotalFormTemplateDiskSize(),
					getTotalImageDiskSize(),
					getTotalOptionDiskSize(),
					getTotalProjectDiskSize(),
					getTotalUserDiskSize(),
				]);
				const usedSize = totalSizes.reduce((prevValue, currentNumber) => prevValue + currentNumber, 0);
				const estimatedSize = usedSize * 1.2; // Add 20%
				const readable = humanFileSize(estimatedSize);
				setEstimateReadable(readable);
			}
		}
		if (persisted) {
			getQuota();
		}
	}, [persisted]);

	useEffect(() => {
		async function refresh() {
			try {
				// Force refresh forces amplify to use the user's refresh token to get a new session
				await fetchAuthSession({ forceRefresh: true });
			} catch (err) {
				// Error refreshing session, needs to log in again
				try {
					await signOut();
				} catch (signoutErr) {
					// Yeah wtf do we do if it fails here?
					// "@throws — AuthTokenConfigException - Thrown when the token provider config is invalid."
					recordSentry({ error: signoutErr, msg: "Failed to sign out" });
				}
			}
		}
		if (online) {
			refresh();
		}
	}, [online]);

	async function setUserOffline(userDoc: User) {
		setLoading(true);
		await replaceUserInDatabase(userDoc);
		window.localStorage.setItem("online", "false");
		setOnline(false);
		setLoading(false);
	}

	async function setUserOnline() {
		if (!online) {
			try {
				setLoading(true);
				const response = await checkNetworkConnection();
				if (response.ok) {
					window.localStorage.setItem("online", "true");
					setOnline(true);
					setLoading(false);
				} else {
					throw new Error("Unexpected response from network request!");
				}
			} catch (error) {
				setLoading(false);
				setNotConnected(true);
				console.error("You're not online");
			}
		}
	}

	function clearNotConnected() {
		setNotConnected(false);
		// Passing true is a way to hard reload, but it's only supported in firefox, which is why ts yells at it
		// @ts-ignore
		window.location.reload(true);
	}

	return {
		clearNotConnected,
		estimateReadable,
		loading,
		notConnected,
		online,
		persisted,
		quota,
		quotaReadable,
		setUserOffline,
		setUserOnline,
		usage,
		usageReadable,
		userErrorMessage,
	};
}

export default useOffline;
